import {
  RouteGetCategories_categoryId_Stores_storeId_FixturesPog,
  type ResponseGetCategories_categoryId_Stores_storeId_FixturesPog,
} from "@CommonApi/categories/_categoryId_/stores/_storeId_/fixturesPog";
import {
  RoutePutCategories_categoryId_Stores_storeId_AisleBreaks_afterBayNo_,
  type ResponsePutCategories_categoryId_Stores_storeId_AisleBreaks_afterBayNo_,
} from "@CommonApi/categories/_categoryId_/stores/_storeId_/aisle-breaks/_afterBayNo_";
import {
  RoutePutCategories_categoryId_AisleBreakBrandLevel,
  type PayloadPutCategories_categoryId_AisleBreakBrandLevel,
  type ResponsePutCategories_categoryId_AisleBreakBrandLevel,
} from "@CommonApi/categories/_categoryId_/aisle-break-brand-level";
import {
  RouteGetMerchflows_merchflowId_StoresAll,
  type ResponseGetMerchflows_merchflowId_StoresAll,
} from "@CommonApi/merchflows/_merchflowId_/stores/all";
import type { OneStoreViewRow } from "@CommonTypes/merchflow/OneStoreView";
import type { PsaAisleSplitSuggestion } from "@CommonUtils/psa/aisleSplitGap";
import { routeToUrl } from "@CommonUtils/routes";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API, getRequest, putRequest } from "src/utils/axios";

export const AISLE_BREAK_SUGGESTIONS_QUERY_ROOT = "aisle-break-suggestions";

export const aisleBreakSuggestionsQueryKey = (categoryId: number, storeId: number) =>
  [AISLE_BREAK_SUGGESTIONS_QUERY_ROOT, categoryId, storeId] as const;

export interface AisleBreakTarget {
  categoryId: number;
  storeId: number;
}

export interface StoreAisleBreakSuggestions {
  suggestions: PsaAisleSplitSuggestion[];
  /** The store's own bay count, which the suggestions are numbered against. */
  bayCount: number;
}

/** The store's fixtures GET, read only for its suggestions: a page whose canvas is not those fixtures. */
export const useAisleBreakSuggestionsQuery = (target: AisleBreakTarget | null) =>
  useQuery({
    queryKey: aisleBreakSuggestionsQueryKey(target?.categoryId ?? 0, target?.storeId ?? 0),
    queryFn: async ({ signal }): Promise<StoreAisleBreakSuggestions> => {
      if (!target) return { suggestions: [], bayCount: 0 };
      const { data } =
        await getRequest<ResponseGetCategories_categoryId_Stores_storeId_FixturesPog>(
          `${API}${RouteGetCategories_categoryId_Stores_storeId_FixturesPog}`,
          { params: { categoryId: target.categoryId, storeId: target.storeId }, signal },
        );
      return {
        suggestions: data.aisle_break_suggestions ?? [],
        bayCount: data.planogram.bays.length,
      };
    },
    enabled: target !== null,
    retry: false,
  });

interface DeclareAisleBreakVariables extends AisleBreakTarget {
  afterBayNo: number;
}

const declareAisleBreak = async ({ categoryId, storeId, afterBayNo }: DeclareAisleBreakVariables) =>
  (
    await putRequest<ResponsePutCategories_categoryId_Stores_storeId_AisleBreaks_afterBayNo_>(
      routeToUrl(`${API}${RoutePutCategories_categoryId_Stores_storeId_AisleBreaks_afterBayNo_}`, {
        categoryId,
        storeId,
        afterBayNo,
      }),
      { payload: undefined },
    )
  ).data;

const DECLARE_AISLE_BREAK_MUTATION_KEY = ["declare-aisle-break"] as const;

export const useDeclareAisleBreakMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DECLARE_AISLE_BREAK_MUTATION_KEY,
    mutationFn: declareAisleBreak,
    onSuccess: (_response, { categoryId, storeId }) => {
      void queryClient.invalidateQueries({
        queryKey: aisleBreakSuggestionsQueryKey(categoryId, storeId),
      });
    },
  });
};

/** True while any aisle break declaration is in flight, wherever its button is. */
export const useIsDeclaringAisleBreak = () =>
  useIsMutating({ mutationKey: DECLARE_AISLE_BREAK_MUTATION_KEY }) > 0;

interface SaveBrandLevelVariables {
  categoryId: number;
  payload: PayloadPutCategories_categoryId_AisleBreakBrandLevel;
}

const saveBrandLevel = async ({ categoryId, payload }: SaveBrandLevelVariables) =>
  (
    await putRequest<ResponsePutCategories_categoryId_AisleBreakBrandLevel>(
      routeToUrl(`${API}${RoutePutCategories_categoryId_AisleBreakBrandLevel}`, { categoryId }),
      { payload },
    )
  ).data;

export const useSaveAisleBreakBrandLevelMutation = () =>
  useMutation({ mutationFn: saveBrandLevel });

const RUN_ROWS_PAGE_SIZE = 500;

/** Every row of these store generation runs, all pages: a refused group is the run's, not the
 *  page on screen. */
export const useStoreGenerationRunRowsQuery = (
  merchflowId: number,
  storePogTriggerIds: readonly number[],
) =>
  useQuery({
    queryKey: ["store-generation-run-rows", merchflowId, ...storePogTriggerIds],
    queryFn: async ({ signal }) => {
      const rows: OneStoreViewRow[] = [];
      for (const storePogTriggerId of storePogTriggerIds) {
        for (let page = 1, totalPages = 1; page <= totalPages; page += 1) {
          const { data } = await getRequest<ResponseGetMerchflows_merchflowId_StoresAll>(
            `${API}${RouteGetMerchflows_merchflowId_StoresAll}`,
            {
              params: { merchflowId },
              queryParams: {
                store_pog_trigger_id: storePogTriggerId,
                viewMode: "list",
                page,
                size: RUN_ROWS_PAGE_SIZE,
              },
              signal,
            },
          );
          rows.push(...data.data);
          totalPages = data.totalPages;
        }
      }
      return rows;
    },
    enabled: storePogTriggerIds.length > 0,
    retry: false,
  });
